import { useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, ParallaxY, Reveal, SectionHeader, Tilt } from "@/components/Reveal";
import GearMark from "@/components/GearMark";
import BuyerPagePreview from "@/components/BuyerPagePreview";
import OfferTimeline from "@/components/OfferTimeline";
import usePageMeta from "@/hooks/usePageMeta";
import { APPS, BOOKING_URL, CONTACT_EMAIL, appById } from "@/config";

const [APP1, APP2] = APPS;

const CONTENT = {
  [APP1.id]: {
    whatTitle: "Turn agreed deals into AWS Marketplace purchases.",
    what: [
      "When a deal is agreed and the buyer is ready to purchase, the seller sends a page branded as their own. The buyer sees the AWS Marketplace benefits open to them — using committed AWS spend, the standard contract, skipping vendor onboarding, flexible payments, and cloud credits.",
      `Buyers see it as ${APP1.buyerName}, branded as you — they pick the options they want and send the internal emails that get approvals moving, or go straight to their private offer.`,
    ],
    whatChips: [
      "Using committed AWS spend",
      "The standard contract",
      "Skipping vendor onboarding",
      "Flexible payments",
      "Cloud credits",
    ],
    whoChip: "For channel partners",
    whoTitle: "Built for channel partners first.",
    whoCopy:
      "Partners carrying AWS Marketplace deals run it today — with ISVs, distributors, and brokers running the same motion right behind them.",
    whoBullets: [
      "Channel partners closing AWS Marketplace deals",
      "ISVs, distributors, and brokers extending the same motion",
      "Sales teams where only top sellers run the Marketplace today",
    ],
    benefits: [
      [
        "Larger deals",
        "Committed AWS spend and flexible payment options free up budget for bigger purchases.",
      ],
      [
        "Faster closes",
        "The standard contract means no new-vendor onboarding between yes and order.",
      ],
      [
        "Every rep runs it",
        "The motion only your top sellers run today becomes the one every rep runs.",
      ],
    ],
    steps: [
      ["Send", "Send your branded link when the deal is ready to buy."],
      ["Choose", "Your buyer chooses their options — or skips straight to the private offer."],
      ["Hand off", `The deal hands off to ${APP2.name} to build the offer.`],
    ],
    motif: "amplify",
  },
  [APP2.id]: {
    whatTitle: "From agreed deal to accurate private offer.",
    what: [
      `${APP2.name} turns an agreed deal into an accurate private offer. Its agent matches the deal to the right incentive playbook, drafts the offer and the authorization, and stops for your approval.`,
      "Once approved, it releases the offer and tracks it to acceptance — with a full timeline and a signed audit trail on every deal.",
    ],
    whoChip: "For channel partners, ISVs, distributors, and brokers",
    whoTitle: "Built for the people who build the offers.",
    whoCopy:
      "If private offers are how your deals close on the AWS Marketplace, the autopilot is how they close right the first time — every time.",
    whoBullets: [
      "Channel partners turning agreed deals into offers",
      "ISVs, distributors, and brokers running offer volume",
      "Teams that need every offer approved and auditable",
    ],
    benefits: [
      [
        "Right the first time",
        "Offers are drafted from the matching incentive playbook, so accuracy is the default.",
      ],
      [
        "A human approval gate",
        "Nothing is released until you approve it — the agent stops and waits.",
      ],
      [
        "One timeline per deal",
        "A full timeline and signed audit trail for every offer, from draft to acceptance.",
      ],
    ],
    steps: [
      ["Enter", `A deal arrives from ${APP1.buyerName}, or you enter it.`],
      ["Draft", "The agent drafts the offer using the matching playbook."],
      ["Approve & track", "You approve, it's released, and you track it to acceptance."],
    ],
    motif: "sync",
  },
};

function NameAsHeadline({ name }) {
  const words = name.split(" ");
  const last = words.pop();
  return (
    <span>
      {words.join(" ")}{" "}
      <em className="font-accent text-[1.2em] font-medium italic tracking-normal text-vermillion">{last}</em>
    </span>
  );
}

function SignInButton({ app, testid, className = "" }) {
  return (
    <a
      data-testid={testid}
      href={app.loginUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`group inline-flex items-center justify-center gap-2.5 rounded-full bg-vermillion px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:bg-vermillion-deep hover:shadow-[0_18px_44px_-14px_rgba(214,53,10,0.65)] active:scale-[0.98] ${className}`}
    >
      Sign in
      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
    </a>
  );
}

export default function AppLanding({ appId }) {
  const app = appById(appId);
  const c = CONTENT[appId];
  const [sp] = useSearchParams();
  const partnerName = sp.get("partner") || "";
  const logoUrl = sp.get("logo") || "";
  usePageMeta(`${app.name} — Transactr`, `${app.tagline} ${c.whoChip}.`, `/og-${app.id}.png`);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(214,53,10,0.06),transparent_55%)]" />
        <div className="container-t relative grid gap-14 pb-20 pt-32 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:pb-28 lg:pt-40">
          <div>
            <Reveal y={14}>
              <p className="eyebrow" data-testid={`${app.id}-eyebrow`}>
                <span className="eyebrow-mark" />
                Transactr app · {app.shortName}
              </p>
            </Reveal>
            <h1
              data-testid={`${app.id}-headline`}
              className="mt-7 font-display text-[clamp(2.1rem,3.6vw,3.2rem)] font-extrabold leading-[1.08] tracking-[-0.02em] text-ink"
            >
              <MaskedLine delay={0.08}>
                <NameAsHeadline name={app.name} />
              </MaskedLine>
            </h1>
            <Reveal delay={0.3} y={18} className="mt-7">
              <p className="max-w-xl text-base leading-relaxed text-inksoft lg:text-lg">
                {app.tagline}
              </p>
            </Reveal>
            <Reveal delay={0.45} y={18} className="mt-10 flex flex-wrap items-center gap-4">
              <SignInButton app={app} testid={`${app.id}-signin-btn`} />
              <a
                data-testid={`${app.id}-book-demo-btn`}
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold tracking-wide text-ink transition-all duration-300 hover:border-ink hover:bg-ink hover:text-paper active:scale-[0.98]"
              >
                Book a demo
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
            </Reveal>
            <Reveal delay={0.55} y={14} className="mt-14">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-line pt-6">
                {["Autonomous", "Marketplace-native", "Human-approved"].map((chip) => (
                  <span
                    key={chip}
                    className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-inksoft"
                  >
                    <span className="h-1.5 w-1.5 rotate-45 bg-vermillion" />
                    {chip}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <Tilt max={3}>
            <ParallaxY from={18} to={-18}>
              <div data-testid={`${app.id}-visual`} className="mx-auto w-full max-w-[500px] rounded-[2rem]">
                {app.id === APP1.id ? (
                  <BuyerPagePreview partnerName={partnerName} logoUrl={logoUrl} />
                ) : (
                  <div className="overflow-hidden rounded-[2rem] border border-line bg-parchment p-4 shadow-[0_40px_90px_-50px_rgba(31,28,25,0.35)] sm:p-6">
                    <OfferTimeline />
                  </div>
                )}
              </div>
              <p className="mt-5 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-inksoft/70">
                Fig. 0{APPS.findIndex((a) => a.id === app.id) + 1} — {app.shortName}
              </p>
            </ParallaxY>
          </Tilt>
        </div>
      </section>

      {/* WHAT IT DOES */}
      <section className="container-t py-24 lg:py-32" data-testid={`${app.id}-what-section`}>
        <SectionHeader eyebrow="What it does" title={c.whatTitle} />
        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          {c.what.map((p, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <p className="text-lg leading-relaxed text-inksoft">{p}</p>
            </Reveal>
          ))}
        </div>
        {c.whatChips ? (
          <Reveal delay={0.2} className="mt-10">
            <div className="flex flex-wrap gap-3">
              {c.whatChips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-line bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-inksoft"
                >
                  {chip}
                </span>
              ))}
            </div>
          </Reveal>
        ) : null}
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-parchment/60 py-24 lg:py-32" data-testid={`${app.id}-who-section`}>
        <div className="container-t grid items-center gap-12 lg:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <p className="eyebrow">
              <span className="eyebrow-mark" />
              {c.whoChip}
            </p>
            <h2 className="mt-5 font-display text-3xl font-bold leading-[1.12] tracking-[-0.02em] text-ink lg:text-[2.6rem]">
              {c.whoTitle}
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-inksoft lg:text-lg">
              {c.whoCopy}
            </p>
            <div className="mt-7 h-px w-24 bg-bluerule" />
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="rounded-3xl border border-line bg-white p-8 lg:p-10">
              {c.whoBullets.map((b, i) => (
                <li
                  key={b}
                  className={`flex items-start gap-3 py-4 text-base text-ink ${
                    i > 0 ? "border-t border-line" : ""
                  }`}
                >
                  <ArrowUpRight size={16} className="mt-0.5 shrink-0 text-vermillion" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* KEY BENEFITS */}
      <section className="container-t py-24 lg:py-32" data-testid={`${app.id}-benefits`}>
        <SectionHeader eyebrow="Key benefits" title="What changes on day one." />
        <div className="mt-14 grid gap-5 lg:grid-cols-3">
          {c.benefits.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col rounded-3xl border border-line bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(31,28,25,0.35)] lg:p-10">
                <span className="font-mono text-[11px] text-vermillion">0{i + 1}</span>
                <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-ink lg:text-2xl">
                  {t}
                </h3>
                <p className="mt-3.5 leading-relaxed text-inksoft">{d}</p>
                <div className="mt-auto pt-7">
                  <div className="h-px w-12 bg-bluerule" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-parchment/60 py-24 lg:py-32" data-testid={`${app.id}-how-it-works`}>
        <div className="container-t">
          <SectionHeader eyebrow="How it works" title="Three steps. Then it runs." />
          <div className="mt-14 grid gap-10 lg:grid-cols-3">
            {c.steps.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <div className="relative border-t border-line pt-8">
                  <span className="font-mono text-4xl font-medium text-vermillion">0{i + 1}</span>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-ink">{t}</h3>
                  <p className="mt-3 leading-relaxed text-inksoft">{d}</p>
                  {i < 2 ? (
                    <span className="absolute -right-5 top-8 hidden h-px w-8 bg-bluerule lg:block" />
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-coal text-white" data-testid={`${app.id}-final-cta`}>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-bluerule/50" />
        <GearMark
          size={560}
          className="pointer-events-none absolute -right-28 -top-28 animate-spin-slower opacity-[0.09]"
        />
        <div className="container-t relative py-24 lg:py-28">
          <Reveal>
            <p className="eyebrow text-bluerule">
              <span className="eyebrow-mark" />
              {app.shortName}
            </p>
            <h2 className="mt-6 max-w-2xl font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.02em] lg:text-[3.2rem]">
              Ready when <span className="font-accent font-medium italic text-vermillion">you</span> are.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/70 lg:text-lg">
              Sign in to the app, or book a walkthrough with the founder and see the motion on your own
              pipeline.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <SignInButton app={app} testid={`${app.id}-final-signin-btn`} className="!px-8 !py-4" />
              <a
                data-testid={`${app.id}-final-book-demo-btn`}
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold tracking-wide text-white transition-all duration-300 hover:border-white/60 hover:bg-white/10 active:scale-[0.98]"
              >
                Book a demo
                <ArrowRight size={16} />
              </a>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
              or email{" "}
              <a
                data-testid={`${app.id}-final-email-link`}
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-bluerule underline-offset-4 transition-colors hover:text-white hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
